import { useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useForkliftDetail } from '@/hooks/useForkliftDetail'
import { useRobotEvents } from '@/hooks/useRobotEvents'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import TrajectoryCanvas from '@/components/maps/TrajectoryCanvas'
import TimeRangeSlider from '@/components/common/TimeRangeSlider'
import { LogTable, LogDetailModal } from '@/components/logs'
import TaskRunTable from '@/components/robotEvents/TaskRunTable'
import ConfigChangeTable from '@/components/robotEvents/ConfigChangeTable'
import CalibrationLogTable from '@/components/robotEvents/CalibrationLogTable'
import CalibrationDetailModal from '@/components/robotEvents/CalibrationDetailModal'
import type { FrontendLog } from '@/types/frontendLog'
import type { CalibrationRecord, TaskRun } from '@/types/robotEvents'

// 実行開始と scenario_execute ログの突き合わせ許容差。UI はログ送信後に
// コマンドを送るため通常は数秒差だが、余裕を持たせる
const RUN_LOG_MATCH_WINDOW_MS = 120 * 1000

export default function ForkliftDetailPage() {
  const fd = useForkliftDetail()
  // 機体イベントジャーナル由来のタスク実行一覧 (対応機体のみデータが返る)
  const robotEvents = useRobotEvents(
    fd.forkId,
    fd.trajectoryStart,
    fd.trajectoryEnd
  )
  // 右パネルの表示切替 (ログ / タスク実行 / 設定変更 / キャリブレーション)
  const [rightTab, setRightTab] = useState<
    'logs' | 'runs' | 'config' | 'calibration'
  >('logs')
  // キャリブレーション行の詳細 (URDF 全文)
  const [selectedCalibration, setSelectedCalibration] =
    useState<CalibrationRecord | null>(null)

  // シナリオ実行 run と UI の scenario_execute ログの突き合わせ
  // (ルートプレビュー・コンテキスト表示用。API/PLC 実行は UI ログが無いので対象外)
  const runLogs = useMemo(() => {
    const logs = fd.logsData?.data ?? []
    const map: Record<string, FrontendLog> = {}
    for (const run of robotEvents.runs) {
      if (!run.isScenario || !run.startTs) continue
      const startMs = Date.parse(run.startTs)
      let best: FrontendLog | undefined
      let bestDiff = RUN_LOG_MATCH_WINDOW_MS
      for (const log of logs) {
        if (log.action !== 'scenario_execute') continue
        if (run.scenarioId && log.context?.scenarioId !== run.scenarioId) {
          continue
        }
        const diff = Math.abs(Date.parse(log.timestamp) - startMs)
        if (diff < bestDiff) {
          bestDiff = diff
          best = log
        }
      }
      if (best) map[run.key] = best
    }
    return map
  }, [fd.logsData, robotEvents.runs])

  // 逆引き (scenario_execute ログの log_id → run)。ログ詳細モーダルで実走行パスを出す
  const logRuns = useMemo(() => {
    const map: Record<string, TaskRun> = {}
    for (const run of robotEvents.runs) {
      const log = runLogs[run.key]
      if (log) map[log.log_id] = run
    }
    return map
  }, [runLogs, robotEvents.runs])

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center gap-x-4 gap-y-2 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 whitespace-nowrap">
          {fd.forkId}
        </h1>
        {fd.siteId && (
          <span className="text-sm text-gray-500 whitespace-nowrap">
            サイト: {fd.siteId}
          </span>
        )}
        {fd.isAnyFetching && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <LoadingSpinner size="sm" />
            <span>読み込み中...</span>
          </div>
        )}
      </div>

      {/* 日付選択と時間範囲スライダー */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* 日付選択 */}
          <div className="shrink-0">
            <input
              type="date"
              value={fd.selectedDate}
              onChange={(e) => fd.handleDateChange(e.target.value)}
              disabled={fd.isAnyFetching}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
            />
          </div>

          {/* 時間範囲スライダー */}
          <div className="flex-1 min-w-0">
            <TimeRangeSlider
              minTime={fd.trajectoryBounds.minTime}
              maxTime={fd.trajectoryBounds.maxTime}
              startTime={fd.trajectoryStart}
              endTime={fd.trajectoryEnd}
              onChange={fd.handleTrajectoryRangeChange}
              disabled={fd.fetchingPositions}
              histogram={fd.allDayPositions?.histogram}
              currentPlaybackTime={fd.currentPlaybackTime}
              markers={fd.scenarioMarkers}
              onMarkerClick={fd.handleMarkerClick}
              snapMs={30 * 60 * 1000}
            />
          </div>
        </div>
      </div>

      {/* 移動軌跡とフロントエンドログを横並び */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        {/* 移動軌跡 */}
        <div
          className={`bg-white rounded-lg shadow p-6 relative ${fd.fetchingPositions ? 'opacity-60' : ''}`}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">移動軌跡</h2>
          </div>
          {fd.loadingPositions ? (
            <div className="flex flex-col items-center justify-center h-64 gap-2">
              <LoadingSpinner size="lg" />
              <span className="text-gray-600">位置データを取得中...</span>
            </div>
          ) : (fd.positions && fd.positions.positions.length > 0) ||
            fd.previewPaths.length > 0 ||
            fd.bgImage ? (
            <div className="relative">
              <TrajectoryCanvas
                positions={fd.positions?.positions ?? []}
                onCurrentTimeChange={fd.setCurrentPlaybackTime}
                targetTime={fd.targetSeekTime}
                previewPaths={fd.previewPaths}
                bgImage={fd.bgImage}
                bgImageCanvasParams={fd.bgImageCanvasParams}
                mapPoles={fd.mapPoles}
                poleSnapshots={fd.poleSnapshots}
              />
              {(!fd.positions || fd.positions.positions.length === 0) &&
                fd.previewPaths.length === 0 && (
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white/85 px-3 py-1 rounded text-sm text-gray-500 pointer-events-none">
                    指定期間の位置データがありません
                  </div>
                )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              指定期間の位置データがありません
            </div>
          )}
        </div>

        {/* フロントエンドログ */}
        <div
          className={`bg-white rounded-lg shadow p-6 sticky top-[5.5rem] max-h-[calc(100vh-22rem)] flex flex-col overflow-hidden ${fd.fetchingLogs ? 'opacity-60' : ''}`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setRightTab('logs')}
                className={`px-3 py-1 rounded-lg text-sm font-semibold transition-colors ${
                  rightTab === 'logs'
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                ログ
              </button>
              {robotEvents.runs.length > 0 && (
                <button
                  onClick={() => setRightTab('runs')}
                  className={`px-3 py-1 rounded-lg text-sm font-semibold transition-colors ${
                    rightTab === 'runs'
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  タスク実行
                  <span className="ml-1 text-xs font-normal">
                    {robotEvents.runs.length}
                  </span>
                </button>
              )}
              {robotEvents.configChanges.length > 0 && (
                <button
                  onClick={() => setRightTab('config')}
                  className={`px-3 py-1 rounded-lg text-sm font-semibold transition-colors ${
                    rightTab === 'config'
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  設定変更
                  <span className="ml-1 text-xs font-normal">
                    {robotEvents.configChanges.length}
                  </span>
                </button>
              )}
              {robotEvents.calibrations.length > 0 && (
                <button
                  onClick={() => setRightTab('calibration')}
                  className={`px-3 py-1 rounded-lg text-sm font-semibold transition-colors ${
                    rightTab === 'calibration'
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  キャリブレーション
                  <span className="ml-1 text-xs font-normal">
                    {robotEvents.calibrations.length}
                  </span>
                </button>
              )}
            </div>
            {rightTab === 'logs' && fd.scenarioLogs.length > 0 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={fd.handlePrevScenario}
                  disabled={fd.currentScenarioIndex <= 0}
                  className={`p-1 rounded transition-colors ${
                    fd.currentScenarioIndex > 0
                      ? 'hover:bg-gray-100 text-gray-600'
                      : 'text-gray-300 cursor-not-allowed'
                  }`}
                  title="前のシナリオ"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-500 min-w-[3rem] text-center">
                  {fd.currentScenarioIndex >= 0
                    ? fd.currentScenarioIndex + 1
                    : 0}{' '}
                  / {fd.scenarioLogs.length}シナリオ
                </span>
                <button
                  onClick={fd.handleNextScenario}
                  disabled={
                    fd.currentScenarioIndex >= fd.scenarioLogs.length - 1
                  }
                  className={`p-1 rounded transition-colors ${
                    fd.currentScenarioIndex < fd.scenarioLogs.length - 1
                      ? 'hover:bg-gray-100 text-gray-600'
                      : 'text-gray-300 cursor-not-allowed'
                  }`}
                  title="次のシナリオ"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
          {rightTab === 'calibration' ? (
            <div className="overflow-y-auto flex-1 min-h-0">
              <CalibrationLogTable
                records={robotEvents.calibrations}
                truncated={robotEvents.truncated}
                onSelect={setSelectedCalibration}
              />
            </div>
          ) : rightTab === 'config' ? (
            <div className="overflow-y-auto flex-1 min-h-0">
              <ConfigChangeTable
                changes={robotEvents.configChanges}
                truncated={robotEvents.truncated}
                onSeek={fd.handleMarkerClick}
              />
            </div>
          ) : rightTab === 'runs' ? (
            <div className="overflow-y-auto flex-1 min-h-0">
              <TaskRunTable
                runs={robotEvents.runs}
                truncated={robotEvents.truncated}
                onSeek={fd.handleMarkerClick}
                compact={true}
                runLogs={runLogs}
                onOpenLog={fd.setSelectedLog}
              />
            </div>
          ) : fd.loadingLogs ? (
            <div className="flex flex-col items-center justify-center h-64 gap-2">
              <LoadingSpinner size="lg" />
              <span className="text-gray-600">ログを取得中...</span>
            </div>
          ) : fd.logsData && fd.logsData.data.length > 0 ? (
            <div className="overflow-y-auto flex-1 min-h-0">
              <LogTable
                logs={fd.logsData.data}
                onDetailClick={fd.setSelectedLog}
                onRowClick={fd.handleLogRowClick}
                highlightScenarioStart={true}
                compact={true}
                showAction={false}
                highlightedLogId={fd.selectedLogId}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              指定日のログがありません
            </div>
          )}
        </div>
      </div>

      {/* キャリブレーション詳細モーダル (URDF 全文) */}
      {selectedCalibration && (
        <CalibrationDetailModal
          record={selectedCalibration}
          onClose={() => setSelectedCalibration(null)}
        />
      )}

      {/* ログ詳細モーダル */}
      {fd.selectedLog && (
        <LogDetailModal
          log={fd.selectedLog}
          onClose={() => fd.setSelectedLog(null)}
          logs={fd.logsData?.data}
          onNavigate={fd.setSelectedLog}
          run={logRuns[fd.selectedLog.log_id]}
        />
      )}
    </div>
  )
}
